import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation, model, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { BaseCrmModelDrivenFormComponent } from './base-model-driven-form.component';
import { CrmFormButtonGroupComponent } from './form-button-group.component';
import { CrmFormLoadingIndicatorComponent } from './form-loading-indicator.component';
import { RendererSelectorComponent } from '../../renderer-selector.component';
import { VerticalNavTabComponent, VerticalNavCardComponent } from '../../../vertical-nav-tab';
import {
  AssociationFormField,
  CrmObjectInjectionToken,
  FieldGroupInterface,
  FieldSchemaInterface,
} from '../../../../tokens-and-interfaces';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CrmObjectAssociationFormFieldComponent } from '../../../association-form-field';
import { getObjectDependencies } from '../../../../utils';
import { SNACKBAR_DURATION } from '../../../../constants';

import { TranslateByObjectTypePipe } from '../../../../i18n';

@Component({
  selector: 'crm-card-group-driven-form',
  templateUrl: './card-group-driven-form.component.html',
  styleUrls: ['./base-model-driven-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyEmptyStateModule,
    GalaxyStickyFooterModule,
    CrmFormButtonGroupComponent,
    CrmFormLoadingIndicatorComponent,
    RendererSelectorComponent,
    VerticalNavTabComponent,
    VerticalNavCardComponent,
    GalaxyFormFieldModule,
    CrmObjectAssociationFormFieldComponent,
    TranslateByObjectTypePipe,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrmCardGroupDrivenFormComponent extends BaseCrmModelDrivenFormComponent {
  protected readonly associationFormFields = model<AssociationFormField[]>([]);
  protected readonly associationIsRequired = signal(false);
  protected readonly associationRequiredError = signal(false);

  private readonly crmObjectDependencies = inject(CrmObjectInjectionToken, { optional: true });

  protected associationsUpdated() {
    this.associationRequiredError.set(this.associationFieldHasError());
  }

  protected override initialize(fieldSchemas: FieldSchemaInterface[], fieldGroups: FieldGroupInterface[]): void {
    super.initialize(fieldSchemas, fieldGroups);
    const createAssociationFields = this.crmObjectDependencies
      ? this.crmObjectDependencies.createAssociationFields
      : getObjectDependencies(this.objectType, this.config)?.createAssociationFields;
    if (createAssociationFields && !this.isEditing) {
      this.associationIsRequired.set(createAssociationFields.required || false);
      const formFields: AssociationFormField[] = createAssociationFields.objectTypes.map((associationType) => {
        return {
          type: associationType,
          associations: [],
        };
      });
      this.associationFormFields.set(formFields);
    }
  }

  private associationFieldHasError(): boolean {
    if (!this.associationIsRequired()) {
      return false;
    }
    const associations = this.associationFormFields().flatMap((fields) => fields.associations);
    return associations.length === 0;
  }

  protected override onSubmit(): void {
    if (this.associationFieldHasError()) {
      const errorMessage = this.translateByObjectTypeService.getTranslationByObjectType(
        this.objectType,
        'CREATE_PAGE.SAVE.ASSOCIATIONS_REQUIRED',
      );
      this.snackService.openErrorSnack(errorMessage, {
        duration: SNACKBAR_DURATION,
      });
      this.associationRequiredError.set(true);
      return;
    }
    super.onSubmit();
  }
}
